/**
 * Cấu hình AI của NGƯỜI DÙNG (BYOK — dùng khoá API của chính mình).
 *
 * Khoá được lưu trong localStorage của trình duyệt và gửi kèm HEADER ở mỗi lời gọi AI.
 * Không gửi lên để lưu ở máy chủ, không nằm trong URL, không ghi vào bài học/xuất file.
 *
 * PHẠM VI LƯU: localStorage tách theo origin nên mỗi website (ebook/lab/pmaker) phải
 * nhập riêng; xoá dữ liệu trang hoặc dùng cửa sổ ẩn danh sẽ mất cấu hình.
 *
 * CẢNH BÁO: mọi thứ trong localStorage đều đọc được bằng JavaScript chạy trên trang này.
 * Đây là đánh đổi có chủ ý để khoá còn dùng lại được sau khi đóng trình duyệt.
 */

export type AIProvider = 'openai' | 'gemini'

export interface ProviderConfig {
  apiKey: string
  model: string
}

export interface AIConfig {
  provider: AIProvider          // nhà cung cấp mặc định cho tác vụ văn bản
  openai: ProviderConfig
  gemini: ProviderConfig
  voiceProvider?: AIProvider    // tác vụ đọc thành tiếng (có thể khác văn bản)
  voiceModel?: string
  videoModel?: string           // model sinh video (Veo — chỉ Gemini)
}

const EMPTY: AIConfig = {
  provider: 'openai',
  openai: { apiKey: '', model: '' },
  gemini: { apiKey: '', model: '' },
}

/** Khoá lưu tách theo TÀI KHOẢN: đổi tài khoản không dùng nhầm khoá của người trước. */
function storageKey(): string {
  let who = 'anon'
  try {
    const raw = localStorage.getItem('auth_user')
    if (raw) who = (JSON.parse(raw)?.email || 'anon').toLowerCase()
  } catch {
    /* auth_user hỏng → coi như chưa đăng nhập */
  }
  return `vatli_ai_config::${who}`
}

export function getAIConfig(): AIConfig {
  try {
    const raw = localStorage.getItem(storageKey())
    if (!raw) return { ...EMPTY }
    const p = JSON.parse(raw)
    return {
      provider: p.provider === 'gemini' ? 'gemini' : 'openai',
      openai: { apiKey: p?.openai?.apiKey || '', model: p?.openai?.model || '' },
      gemini: { apiKey: p?.gemini?.apiKey || '', model: p?.gemini?.model || '' },
      voiceProvider: p.voiceProvider,
      voiceModel: p.voiceModel,
      videoModel: p.videoModel,
    }
  } catch {
    // Trình duyệt chặn lưu trữ (ẩn danh/chặn cookie) → coi như chưa cấu hình.
    return { ...EMPTY }
  }
}

/** Trả về false nếu trình duyệt không cho lưu — nơi gọi phải báo cho người dùng biết. */
export function saveAIConfig(cfg: AIConfig): boolean {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(cfg))
    window.dispatchEvent(new CustomEvent('ai-config-changed'))
    return true
  } catch {
    return false
  }
}

/** Xoá toàn bộ khoá đã lưu TRÊN TRÌNH DUYỆT NÀY cho tài khoản hiện tại. */
export function clearAIConfig(): void {
  try {
    localStorage.removeItem(storageKey())
    window.dispatchEvent(new CustomEvent('ai-config-changed'))
  } catch {
    /* không xoá được thì thôi, không chặn luồng */
  }
}

export function hasAIKey(provider?: AIProvider): boolean {
  const c = getAIConfig()
  const p = provider || c.provider
  return !!(c[p].apiKey && c[p].model)
}

export type AITaskKind = 'text' | 'voice' | 'video'

/**
 * Header gửi kèm lời gọi AI. Trả {} khi chưa cấu hình — nơi gọi tự quyết định
 * báo lỗi hay mở hộp thoại Cấu hình AI.
 *
 * kind='voice'/'video': dùng nhà cung cấp/model riêng cho tác vụ đó nếu người dùng
 * đã đặt; video luôn là Gemini vì Veo là dịch vụ của Google.
 */
export function aiHeaders(kind: AITaskKind = 'text'): Record<string, string> {
  const c = getAIConfig()
  let provider: AIProvider = c.provider
  if (kind === 'voice' && c.voiceProvider) provider = c.voiceProvider
  if (kind === 'video') provider = 'gemini'

  const apiKey = c[provider].apiKey
  let model = c[provider].model
  if (kind === 'voice' && c.voiceModel) model = c.voiceModel

  if (!apiKey || !model) return {}
  return { 'X-AI-Provider': provider, 'X-AI-Key': apiKey, 'X-AI-Model': model }
}

/** Model sinh video (Veo). Rỗng = để backend dùng mặc định. */
export function aiVideoModel(): string {
  return getAIConfig().videoModel || ''
}

/** 428 = backend báo chưa có khoá → mở hộp thoại Cấu hình AI. */
export const AI_CONFIG_REQUIRED = 428

export function openAIConfigDialog(): void {
  window.dispatchEvent(new CustomEvent('open-ai-config'))
}

/**
 * Dùng ở mọi nơi bắt lỗi lời gọi AI: 428 thì mở phần cấu hình thay vì hiện lỗi khó hiểu.
 * Trả về true nếu đã xử lý (đang mở hộp thoại).
 */
export function handleAIError(status?: number): boolean {
  if (status === AI_CONFIG_REQUIRED) {
    openAIConfigDialog()
    return true
  }
  return false
}
